import { EntraErrorCodes, ErrorService, ServerErrorStatus } from '@agilox/common';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthError } from '@azure/msal-browser';
import { UserStoreService } from '@user-store';
import { concatMap, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isInIframe } from './iframe.util';
import { toLoginErrorDetails } from './login-error-details.util';

/**
 * The user is authenticated, so these statuses mean the backend won't return a
 * user (401/403) or is broken (5xx). Re-running the login redirect can't fix any
 * of them and just loops, so we show the error page instead.
 */
function isUnrecoverableUserLoadError(status: number): boolean {
	return status === 401 || status === 403 || status >= 500;
}

function isUnsupportedAccountError(error: unknown): error is AuthError {
	return (
		error instanceof AuthError && error.errorMessage.includes(EntraErrorCodes.UnsupportedAccount)
	);
}

function toServerErrorStatus(status: number): ServerErrorStatus {
	if (status === 401) return ServerErrorStatus.UNAUTHORIZED;
	if (status === 403) return ServerErrorStatus.FORBIDDEN;
	return ServerErrorStatus.SERVER_ERROR;
}

export function userInitializationFactory(
	msalService: MsalService,
	userStoreService: UserStoreService,
	skipLoginRedirect = false
): () => Observable<any> {
	const errorService = inject(ErrorService);
	return () =>
		msalService.handleRedirectObservable().pipe(
			concatMap((response) => {
				let activeAccount = null;

				if (response?.account) {
					activeAccount = response.account;
				} else {
					const accounts = msalService.instance.getAllAccounts();
					if (accounts.length > 0) {
						activeAccount = accounts[0];
					}
				}

				if (activeAccount) {
					msalService.instance.setActiveAccount(activeAccount);

					return userStoreService.loadCurrent().pipe(
						catchError((error) => {
							if (isUnrecoverableUserLoadError(error?.status)) {
								// Show the full-page error instead of looping through login.
								// Emit so the app still bootstraps and can render it.
								errorService.setServerError(toServerErrorStatus(error.status));
								return of(null);
							}
							if (skipLoginRedirect) {
								return EMPTY;
							}
							const inIframe = isInIframe();
							return inIframe ? msalService.loginPopup() : msalService.loginRedirect();
						})
					);
				}

				if (skipLoginRedirect) {
					return EMPTY;
				}
				const inIframe = isInIframe();
				return inIframe ? msalService.loginPopup() : msalService.loginRedirect();
			}),
			catchError((error) => {
				if (isUnsupportedAccountError(error)) {
					errorService.setServerError(
						ServerErrorStatus.UNSUPPORTED_ACCOUNT,
						toLoginErrorDetails(error)
					);
					return of(null);
				}
				return throwError(() => error);
			})
		);
}
